import { eventCategories, events } from '@js/utils/event';
import { arrayToggleValue, logEvent, logEvents, storedItemCreate, storedItemRead } from '@js/utils/toolbox';
import { shallowReactive } from 'vue';

// Initial state, frozen the initial state to prevent mutations
const filters = () => ({
	airportStops: [],
	airportArrivals: [],
	airportDepartures: [],
	departureArrival: {},
	airportWaitTime: null,
	changesAirport: null,
	price: null,
	flexTicket: null,
	paymentMethodIncluded: null,
	baggageIncluded: null,
	cabinBaggageIncluded: null,
	climateCompensated: null,
	departureTime: [],
	arrivalTime: [],
	traveltime: null,
	enabledAgents: [],
	enabledAirlines: [],
	blacklisted: null,
	alliances: [],
	foreign: null,
	numStops: null,
	youthTicket: null,
	isSortByLowestEmissionsEnabled: false
});

// The store object
const state = shallowReactive({
	...filters(),
	isSortByLowestEmissionsEnabled: js_params?.urlVars?.sorting === 'emission',
	youthTicket: js_params?.isYouthSearch === true
});

// Meta object to store additional information about the filters
const meta = reactive({
	lastKeyUpdated: false
});

let filtersBackup = storedItemRead('savedFilters') || {};

// Event mappings
const ENABLE_EVENTS = {
	airportsArrivals: events.filterAirportsArrivalEnabled,
	airportsDepartures: events.filterAirportsDepartureEnabled,
	airportStops: events.filterAirportsStopsEnabled,
	airportWaitTime: events.filterNoLongTransferTimeEnabled,
	alliances: events.filterAllianceEnabled,
	blacklisted: events.filterBlacklistedEnabled,
	enabledAirlines: events.filterAirlinesEnabled,
	travelTime: events.filterTravelTimeEnabled,
	numStops: events.filterStopsEnabled,
	changesAirport: events.filterDirectFlightOnlyEnabled,
	climateCompensated: events.filterCO2CompensateEnabled,
	departureTime: events.filterDepartureTimeEnabled,
	enabledAgents: events.filterTravelCompanyEnabled,
	arrivalTime: events.filterArrivalTimeEnabled,
	activeTimetableBtns: events.filterActiveTimetableEnabled,
	flexTicket: events.filterRebookableEnabled,
	baggageIncluded: events.filterBaggageEnabled,
	cabinBaggageIncluded: events.filterCabinBaggageEnabled,
	paymentMethodIncluded: events.filterPaymentMethodEnabled,
	foreign: events.filterHideForeignTravelAgenciesEnabled,
	// Sorting:
	sortEmission: events.sortByCO2EmissionEnabled
};

const DISABLED_EVENTS = {
	airportsArrivals: events.filterAirportsArrivalDisabled,
	airportsDepartures: events.filterAirportsDepartureDisabled,
	airportStops: events.filterAirportsStopsDisabled,
	airportWaitTime: events.filterNoLongTransferTimeDisabled,
	alliances: events.filterAllianceDisabled,
	blacklisted: events.filterBlacklistedDisabled,
	enabledAirlines: events.filterAirlinesDisabled,
	travelTime: events.filterTravelTimeDisabled,
	numStops: events.filterStopsDisabled,
	changesAirport: events.filterDirectFlightOnlyDisabled,
	climateCompensated: events.filterCO2CompensateDisabled,
	departureTime: events.filterDepartureTimeDisabled,
	enabledAgents: events.filterTravelCompanyDisabled,
	arrivalTime: events.filterArrivalTimeDisabled,
	activeTimetableBtns: events.filterActiveTimetableDisabled,
	flexTicket: events.filterRebookableDisabled,
	baggageIncluded: events.filterBaggageDisabled,
	cabinBaggageIncluded: events.filterCabinBaggageDisabled,
	paymentMethodIncluded: events.filterPaymentMethodDisabled,
	foreign: events.filterHideForeignTravelAgenciesDisabled,
	// Sorting:
	sortEmission: events.sortByCO2EmissionDisabled
};

const activeFilters = computed(() => Object.keys(state).filter(key => isVariableNotEmpty(state[key])));
const isAnyFilterActive = computed(() => activeFilters.value.length > 0);

// Mutations
const methods = {
	/**
	 * Sets a filter in the store
	 * @param {Object} payload - The payload object should contain a key and value property
	 * @param {string} payload.key - The key of the filter to be set
	 * @param {any} payload.value - The value of the filter to be set
	 * @returns {void}
	 */
	set ({ key, value }) {
		// Update state
		state[key] = value;

		// Update the last key updated
		meta.lastKeyUpdated = isVariableNotEmpty(value) ? key : false;

		this.logFilterChange({ [key]: value });

		storedItemCreate('savedFilters', state);
	},

	/**
	 * Sets multiple filters in the store
	 * @param {Array} payload - The payload object should contain a key and value property
	 * @param {string} payload.key - The key of the filter to be set
	 * @param {any} payload.value - The value of the filter to be set
	 * @returns {void}
	 */
	setAll (payload, logEvent = true) {
		// Update state
		Object.assign(state, payload);

		// Update the last key updated
		const key = Object.keys(payload)[0];
		meta.lastKeyUpdated = isVariableNotEmpty(payload[key]) ? key : false;

		if (logEvent) {
			this.logFilterChange(payload);
		}


		storedItemCreate('savedFilters', state);
	},

	toggleEnabled (payload) {
		if (payload) {
			// Restore user filters
			Object.assign(state, filtersBackup);
			filtersBackup = {};
		} else {
			// Backup user filters
			Object.assign(filtersBackup, state);
			// Reset filters to initial state
			Object.assign(state, { ...state, ...filters() });
		}
	},

	resetAll () {
		logEvent(events.resetFilters, eventCategories.filter);

		// If isSortByLowestEmissionsEnabled is set to true, reset the sorting to the default value
		if (state.isSortByLowestEmissionsEnabled) {
			state.isSortByLowestEmissionsEnabled = false;
			window.showresult.updateSorting(js_params.defaultSorting);
		}
		Object.assign(state, filters());

		meta.lastKeyUpdated = false;
	},

	reset (keys) {
		const defaults = filters();

		const itemsToReset = Object.fromEntries(keys.map(key => [key, defaults[key]]));
		Object.assign(state, itemsToReset);

		// If the last key updated is in the keys to reset, set it to false
		if (keys.includes(meta.lastKeyUpdated)) {
			meta.lastKeyUpdated = false;
		}

		if (keys.includes('isSortByLowestEmissionsEnabled')) {
			window.showresult.updateSorting(js_params.defaultSorting);
		}

		logEvent(events.resetFiltersIndividualCategory, eventCategories.filter);
	},

	isFilterActive (filters) {
		return filters.some(filter => activeFilters.value.includes(filter));
	},

	get: computed(() => {
		const filters = Object.fromEntries(activeFilters.value.map(key => [key, state[key]]));

		delete filters.isSortByLowestEmissionsEnabled;
		return filters;
	}),

	logFilterChange (payload) {
		// Iterate over each entry in payload that exists in the event mappings.
		const eventsToLog = Object.entries(payload)
			.filter(([key]) => key in ENABLE_EVENTS) // Only process valid keys
			.map(([key]) => {
				const isActive = this.isFilterActive([key]);
				return {
					event: isActive ? ENABLE_EVENTS[key] : DISABLED_EVENTS[key],
					eventCategory: eventCategories.filter
				};
			});

		logEvents(eventsToLog);
	},

	toggleValue (key, value) {
		const newArray = arrayToggleValue(state[key], value);
		this.set({ key, value: newArray });
	}
};


// Export a Composition API function to use the store
export const useFiltersStore = () => ({
	state,
	meta,
	activeFilters,
	isAnyFilterActive,
	...methods
});

function isVariableNotEmpty (value) {
	if (value == null) {
		return false;
	}
	if (['string', 'boolean', 'number'].includes(typeof value)) {
		return typeof value === 'boolean'
			? value === true // only “true” counts
			: (typeof value === 'number'
				? !isNaN(value) // exclude NaN
				: value.trim() !== ''); // non‑empty strings
	}
	if (Array.isArray(value)) {
		return value.length > 0;
	}
	if (typeof value === 'object') {
		return Object.keys(value).length > 0;
	}
	return false;
}
